/**
 * ResetPassword — accepts ?token=... and lets the user set a new password.
 *
 * Backend validates the token (single-use, 1h expiry) and updates the password.
 * After success the user is redirected to Sign In with a success toast.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api';
import { ChevronLeft, Lock, Eye, EyeOff, ShieldCheck, CheckCircle } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // If no token, do NOT auto-redirect — show a clean error.
  }, []);

  const handleSubmit = async () => {
    setError('');
    if (!token) {
      setError('This reset link is missing its token. Please request a new one.');
      return;
    }
    if (!pwd || pwd.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (pwd !== pwd2) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/auth'), 2500);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Invalid or expired link. Request a new one.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT }}>
      <div className="px-5 py-4 flex items-center">
        <button data-testid="reset-back-btn" onClick={() => navigate('/auth')}
          className="p-1.5 -ml-1.5">
          <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 px-6 pb-10 flex flex-col justify-center max-w-md mx-auto w-full">
        <div className="text-center mb-8">
          <img
            data-testid="reset-brand-logo"
            src="/lunora_logo.png"
            alt="Lunora's"
            className="w-28 h-auto mx-auto mb-3"
            style={{ display: 'block' }}
          />
          <h1 className="text-[28px]"
            style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
            {done ? 'Password updated' : 'Set a new password'}
          </h1>
          <p className="text-[12px] mt-2" style={{ color: MUTED, lineHeight: 1.6 }}>
            {done ? 'You can now sign in with your new password.' : 'Enter your new password below.'}
          </p>
        </div>

        {done ? (
          <div data-testid="reset-success-banner"
            className="flex items-center gap-3 px-4 py-3"
            style={{ background: 'rgba(22,101,52,0.06)', border: '1px solid rgba(22,101,52,0.30)' }}>
            <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={1.6} />
            <p className="text-[11px]" style={{ color: INK }}>
              Password reset successful. Redirecting to sign in…
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: MUTED }}>New Password</label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
                <input
                  data-testid="reset-pwd-input"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={pwd}
                  onChange={(e) => { setPwd(e.target.value); setError(''); }}
                  placeholder="At least 6 characters"
                  className="w-full pl-8 pr-10 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                  style={{ borderColor: LINE, color: INK }} />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-1">
                  {showPwd
                    ? <EyeOff className="w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
                    : <Eye className="w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: MUTED }}>Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
                <input
                  data-testid="reset-pwd2-input"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={pwd2}
                  onChange={(e) => { setPwd2(e.target.value); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                  placeholder="Re-enter password"
                  className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                  style={{ borderColor: LINE, color: INK }} />
              </div>
            </div>

            {error && (
              <p data-testid="reset-error" className="text-[11px] text-center" style={{ color: '#991B1B' }}>
                {error}
              </p>
            )}

            <button data-testid="reset-submit-btn"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full py-4 text-[11px] tracking-[0.18em] uppercase text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}>
              {isLoading ? 'Updating…' : 'Update Password'}
            </button>

            <div className="flex items-center justify-center gap-2 pt-2">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.5} />
              <span className="text-[10px]" style={{ color: MUTED }}>Secure &amp; Encrypted</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
